import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import { useBillingAccess } from '../hooks/useBillingAccess';
import { createBillingAccessApiService } from '../services/BillingAccessApiService';
import SubscriptionProductsList from '../components/SubscriptionProductsList';
import type { BillingProduct } from '../types';

export interface ProjectPaywallProps {
  projectId: string;
  capability: string;
}

/**
 * In-place hard paywall rendered (inside <Layout>) when a non-admin opens a
 * capability-gated project route without an active subscription. Replaces the
 * old `<Navigate to="/all-projects">` bounce. The Subscribe CTA starts a real
 * self-serve subscription checkout; exits to billing / all-projects keep the
 * user un-trapped.
 */
export function ProjectPaywall({ projectId, capability }: ProjectPaywallProps): JSX.Element {
  const navigate = useNavigate();
  const billing = useBillingAccess(projectId);
  const service = React.useMemo(() => createBillingAccessApiService(), []);
  const [products, setProducts] = React.useState<BillingProduct[]>([]);
  const [requiredProducts, setRequiredProducts] = React.useState<BillingProduct[]>([]);
  const [productsLoading, setProductsLoading] = React.useState(false);
  const [submittingProductId, setSubmittingProductId] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const activeProductIds = React.useMemo(
    () => (billing.access?.activeSubscriptions || []).map((subscription) => subscription.productId),
    [billing.access?.activeSubscriptions],
  );

  React.useEffect(() => {
    let cancelled = false;
    setProductsLoading(true);
    service.listSubscriptionProducts(projectId)
      .then((nextProducts) => {
        if (!cancelled) setProducts(nextProducts);
      })
      .catch((err) => {
        if (!cancelled) setError((err as Error).message);
      })
      .finally(() => {
        if (!cancelled) setProductsLoading(false);
      });
    return () => { cancelled = true; };
  }, [projectId, service]);

  // P0-b: name the product(s) that include the missing capability. A lookup failure is
  // non-fatal: the paywall renders exactly as before, without the "Included in" line.
  React.useEffect(() => {
    let cancelled = false;
    setRequiredProducts([]);
    service.listCapabilityProducts(projectId, capability)
      .then((nextProducts) => {
        if (!cancelled) setRequiredProducts(nextProducts);
      })
      .catch((err) => {
        console.warn('[ProjectPaywall] capability product lookup failed', err);
        if (!cancelled) setRequiredProducts([]);
      });
    return () => { cancelled = true; };
  }, [projectId, capability, service]);

  // D-12: required product first, same card style.
  const orderedProducts = React.useMemo(() => {
    const requiredIds = new Set(requiredProducts.map((product) => product.id));
    return [...products].sort((a, b) => Number(requiredIds.has(b.id)) - Number(requiredIds.has(a.id)));
  }, [products, requiredProducts]);

  const handleSubscribe = React.useCallback(async (productId: string) => {
    setSubmittingProductId(productId);
    setError(null);
    try {
      const session = await service.createSubscriptionCheckoutSession(projectId, productId);
      if (!session.url) {
        throw new Error('Stripe checkout did not return a redirect URL');
      }
      window.location.assign(session.url);
    } catch (err) {
      setError((err as Error).message);
      setSubmittingProductId(null);
    }
  }, [projectId, service]);

  if (billing.loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', p: 4, minHeight: '60vh' }}>
      <Paper sx={{ maxWidth: 520, width: '100%', p: 4 }} data-testid="project-paywall" data-capability={capability}>
        <Stack spacing={2}>
          <Typography variant="h5">Subscribe to unlock this project</Typography>
          <Typography color="text.secondary">
            Choose the subscription surface this project needs.
          </Typography>
          {requiredProducts.length > 0 && (
            <Typography data-testid="project-paywall-required-product">
              Included in:{' '}
              {requiredProducts.map((product, index) => (
                <React.Fragment key={product.id}>
                  {index > 0 && ', '}
                  <Box component="strong" sx={{ fontWeight: 600 }}>{product.name}</Box>
                </React.Fragment>
              ))}
            </Typography>
          )}
          {error && <Alert severity="error">{error}</Alert>}
          <SubscriptionProductsList
            products={orderedProducts}
            loading={productsLoading}
            busyProductId={submittingProductId}
            activeProductIds={activeProductIds}
            actionLabel="Subscribe"
            onSubscribe={handleSubscribe}
          />
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Button variant="text" onClick={() => navigate(`/project/${projectId}/billing`)} data-testid="project-paywall-billing">
              View billing
            </Button>
            <Button variant="text" onClick={() => navigate('/all-projects')} data-testid="project-paywall-all-projects">
              All projects
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
}

export default ProjectPaywall;
